// src/pages/subscription/TenantSubscriptionListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  const s = (status || '').replace('TENANT_SUBSCRIPTION_STATUS_', '');
  switch (s) {
    case 'ACTIVE': return 'success';
    case 'TRIALING': case 'PENDING': return 'primary';
    case 'PAST_DUE': case 'PAUSED': return 'warning';
    case 'CANCELED': case 'EXPIRED': case 'SUSPENDED': return 'error';
    default: return 'secondary';
  }
}

export const TenantSubscriptionListPage = observer(() => {
  const store = useSubscription();
  const auth = useAuth();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [items, setItems] = useState<any[]>([]);
  // ListTenantSubscriptions is now scoped to a tenant.
  const tenantId = auth.user?.entity?.tenant_id || '';

  useEffect(() => {
    if (!tenantId) return;
    store.ListTenantSubscriptions(tenantId).then((res) => {
      const list = res?.tenants?.items;
      setItems(Array.isArray(list) ? list : []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tenantId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Tenant Subscriptions</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Tenant Subscriptions</h1>
        </div>
        <Link to="/subscriptions/tenants/new"><Button>Subscribe Tenant</Button></Link>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Seats</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Current Period</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={8} message="No tenant subscriptions found" />
            ) : (
              items.map((item: any) => {
                // Detail routes are keyed by product id (tenant comes from the session).
                const itemId = item.product?.id || item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link to={`/subscriptions/tenants/${itemId}`} className="text-primary hover:underline">
                        {String(item.tenant_name ?? item.tenant_id ?? '--')}
                      </Link>
                    </TableCell>
                    <TableCell>{String(item.product_name ?? item.product_id ?? '--')}</TableCell>
                    <TableCell>{String(item.plan_name ?? item.plan_id ?? '--')}</TableCell>
                    <TableCell>
                      {item.status ? (
                        <Badge variant={statusVariant(item.status)} size="sm">{formatLabel(item.status, 'TENANT_SUBSCRIPTION_STATUS_')}</Badge>
                      ) : '--'}
                    </TableCell>
                    <TableCell>{item.seats != null ? String(item.seats) : '--'}</TableCell>
                    <TableCell>{formatMoney(item.price ?? item.current_price)}</TableCell>
                    <TableCell>
                      {formatDate(item.current_period_start)} - {formatDate(item.current_period_end)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/subscriptions/tenants/${itemId}`}><Button variant="ghost" size="sm">View</Button></Link>
                        <Link to={`/subscriptions/tenants/${itemId}/edit`}><Button variant="ghost" size="sm">{t('common.edit')}</Button></Link>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TenantSubscriptionListPage;
